import { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { RouteAccessGate } from '@/components/routes/RouteAccessGate';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePermissions } from '@/hooks/usePermissions';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { getModuleEntryByErpModule, getModuleLoginPath, resolveModuleLoginPathFromRoute } from '@/config/moduleEntries';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { getLauncherModules, isOrgOwner } from '@/utils/moduleAccess';
import { canAccessWorkspacePath } from '@/utils/moduleNavigation';
import { evaluateModuleScopeDenial } from '@/utils/routeAccessPolicy';
import type { ErpModule } from '@/api/platform.api';

export function ModuleScopeGuard() {
  const masterAdmin = useAppSelector((s) => s.auth.user?.masterAdmin === true);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const workspaceModule = useAppSelector((s) => s.auth.workspaceModule);
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const permissions = useAppSelector((s) => s.auth.user?.permissions ?? []);
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);
  const location = useLocation();
  const { has } = usePermissions();

  if (masterAdmin || location.pathname.startsWith('/master')) {
    return <Outlet />;
  }

  const owner = isOrgOwner(permissions, roleCode);
  const scopedModule: ErpModule | null =
    entryModule ??
    (owner ? resolveModuleFromPath(location.pathname) ?? workspaceModule : null);

  const launcherCodes = useMemo(
    () =>
      getLauncherModules(enabledModules, permissions, accessibleModules, roleCode, has).map(
        (entry) => entry.erpModule,
      ),
    [enabledModules, permissions, accessibleModules, roleCode, has],
  );

  const canAccessPath = useMemo(
    () => (pathname: string) =>
      canAccessWorkspacePath(pathname, enabledModules, launcherCodes, accessibleModules),
    [accessibleModules, enabledModules, launcherCodes],
  );

  const denial = useMemo(
    () =>
      scopedModule
        ? evaluateModuleScopeDenial({ pathname: location.pathname, scopedModule })
        : null,
    [location.pathname, scopedModule],
  );

  const fallbackPath =
    denial?.fallbackPath ??
    (scopedModule ? (getModuleEntryByErpModule(scopedModule)?.homePath ?? MODULE_HUB_PATH) : MODULE_HUB_PATH);

  return (
    <RouteAccessGate
      denied={denial != null}
      message={denial?.message}
      fallbackPath={fallbackPath}
      silent={denial?.silent}
      canAccessPath={canAccessPath}
    >
      <Outlet />
    </RouteAccessGate>
  );
}

export function resolveLoginRedirect(
  pathname: string,
  entryModule: string | null | undefined,
  activeModule?: string | null,
): string {
  if (pathname.startsWith('/master')) {
    return '/master/login';
  }
  const module = entryModule ?? activeModule;
  if (module) {
    return getModuleLoginPath(module);
  }
  return resolveModuleLoginPathFromRoute(pathname) ?? MODULE_HUB_PATH;
}

/** After sign-out — return to the app login the user was working in (e.g. CRM → /crm/login). */
export function resolveLogoutLoginPath(options: {
  masterAdmin?: boolean;
  activeModule?: string | null;
  entryModule?: string | null;
  pathname: string;
}): string {
  if (options.masterAdmin) {
    return '/master/login';
  }
  return resolveLoginRedirect(options.pathname, options.entryModule, options.activeModule);
}
